import { z } from 'zod';

/**
 * Inteligência (docs/specs/063): the report of a day, written by an AI that read the conversations
 * of that day — what happened in each one, how each seller did, whether the SLA was covered, how
 * the CRM was used, and what to fix. Each run leaves a report; the screen picks one from the
 * history. The numbers come from the CRM; what the AI wrote says so (`coverage`, `readBy`).
 * `VITE_API_MOCKS=intelligence` mocks the API in the browser.
 *
 * GET /intelligence/reports → ReportDay[] (the days with a report, newest first)
 * GET /intelligence/reports/:runId → IntelligenceReport
 * GET /intelligence/evolution?days= → IntelligenceEvolution
 */

export const INTELLIGENCE_TABS = [
  'summary',
  'sellers',
  'conversations',
  'recommendations',
  'evolution',
] as const;
export type IntelligenceTab = (typeof INTELLIGENCE_TABS)[number];

/** The URL of the screen: the tab and the day of the report (the newest when absent). */
export const IntelligenceSearchSchema = z.object({
  tab: z.enum(INTELLIGENCE_TABS).optional().catch(undefined),
  day: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
});
export type IntelligenceSearch = z.infer<typeof IntelligenceSearchSchema>;

/** Where a conversation stands, as the AI read it. */
export const SITUATIONS = [
  'no-context',
  'interest',
  'quote-unanswered',
  'negotiating',
  'won',
  'after-sale',
  'lost',
] as const;
export type Situation = (typeof SITUATIONS)[number];

export const PRIORITIES = ['high', 'medium', 'low', 'none'] as const;
export type Priority = (typeof PRIORITIES)[number];

export const CONTACT_KINDS = [
  'customer',
  'professional',
  'company',
  'supplier',
  'internal',
  'other',
] as const;
export type ContactKind = (typeof CONTACT_KINDS)[number];

/** The steps of SPIN Selling, the method the team is judged by. */
export const SPIN_STEPS = ['situation', 'problem', 'implication', 'need'] as const;
export type SpinStep = (typeof SPIN_STEPS)[number];

/** The share 0–1 of the sales conversations where each step was explored. */
export type SpinCoverage = Record<SpinStep, number> & { conversations: number };

/** The habits of using the CRM measured in each member. */
export const PRACTICES = ['dealValue', 'nextStep', 'lossReason', 'tasksOnTime'] as const;
export type Practice = (typeof PRACTICES)[number];

/** A number of the day and of the one before; null when it cannot be told. */
export interface Measured {
  current: number | null;
  previous: number | null;
}

export interface IntelligenceKpis {
  conversations: Measured;
  messagesReceived: Measured;
  messagesSent: Measured;
  /** Median, seconds. */
  responseSeconds: Measured;
  /** Share 0–1 of the first responses due in the day answered in time. */
  slaCoverage: Measured;
  /** Customers still without an answer when the day ended. */
  waiting: Measured;
  onlineSeconds: Measured;
  wonCount: Measured;
  wonCents: Measured;
}

export interface Evidence {
  conversationId: string;
  customer: string;
  /** A line of the conversation, as said. */
  quote: string;
  when: string;
}

/**
 * How to do in the CRM itself what the analyst suggests: the screen, its way there and the
 * steps. The writer picks from a closed catalogue, so it never invents a setting.
 */
export interface CrmSetup {
  target:
    | { kind: 'settings'; id: string }
    | {
        kind: 'page';
        to: '/automations' | '/campaigns' | '/capture/referrals';
        permission: string;
      };
  /** Where it is, as people find it. */
  where: string;
  /** The words of the button that goes there. */
  action: string;
  steps: string[];
  /** The help article, under `/help/`. */
  help: string;
}

/** A finding: `detail` explains the problem, `actions` fix it, `setup` does it in the CRM. */
export interface InsightCard {
  id: string;
  kind: 'risk' | 'opportunity' | 'win';
  area: string;
  title: string;
  detail: string;
  /** Money the finding is worth, when it can be told. */
  impactCents: number | null;
  conversations: number;
  actions: string[];
  setup: CrmSetup | null;
  /** Which setting `setup` is: the button that applies it is found by it. */
  setupId?: string | null;
  /** The lines of the conversations that prove it. */
  evidence: Evidence[];
}

/** How many answers fell in each band of waiting. */
export const RESPONSE_BANDS = ['upTo5', 'upTo15', 'upTo60', 'over60'] as const;
export type ResponseBand = (typeof RESPONSE_BANDS)[number];
export type ResponseBands = Record<ResponseBand, number>;

/** One seller's day. */
export interface SellerDay {
  id: string;
  name: string;
  /** Null = the history of presence did not exist yet on that day. */
  onlineSeconds: number | null;
  firstOnlineAt: string | null;
  lastOnlineAt: string | null;
  conversations: number;
  messagesSent: number;
  messagesPerConversation: number | null;
  /** Of the conversations, those where the customer wrote; absent in older reports. */
  customerConversations?: number;
  /** Of the messages sent, the template ones (broadcasts); absent in older reports. */
  templatesSent?: number;
  /** From the phone in their conversations: the CRM cannot tell who wrote them. */
  sentOutsideCrm: number;
  responseSeconds: number | null;
  responseBands: ResponseBands;
  /** The longest a customer waited for them, among what they answered. */
  longestWaitSeconds: number | null;
  /** Messages sent per local hour, the hours with none left out. */
  hours: { hour: number; messages: number }[];
  /** Tasks due in the day: finished, and still open. */
  tasksDone: number;
  tasksOverdue: number;
  waiting: number;
  sla: { met: number; missed: number } | null;
  wonCount: number;
  wonCents: number;
  lostCount: number;
  actions: number;
  /** 0–100; null when no habit was measured. */
  usageScore: number | null;
  practices: Record<Practice, number | null>;
  /** 0–100, the mean of the AI's notes; null until the reader ran. */
  quality: number | null;
  spin: SpinCoverage | null;
  strengths: string[];
  failures: string[];
  coaching: string | null;
  evidence: Evidence[];
}

/** One conversation of the day: what the CRM counted and what the AI read in it. */
export interface ConversationDay {
  conversationId: string;
  customer: string;
  sellerId: string | null;
  sellerName: string | null;
  lastMessageAt: string;
  messages: {
    received: number;
    sent: number;
    outsideCrm: number;
    bot: number;
    bySeller: { id: string; name: string; messages: number }[];
  };
  responseSeconds: number | null;
  longestWaitSeconds: number | null;
  /** The first response due in the day; null = nothing was due. */
  sla: 'met' | 'missed' | null;
  waiting: boolean;
  /** `ai` = read by the model; `rule` = nothing to read; `none` = not read. */
  readBy: 'ai' | 'rule' | 'none';
  priority: Priority;
  situation: Situation | null;
  contactKind: ContactKind | null;
  summary: string | null;
  chance: number | null;
  chanceReason: string | null;
  potentialCents: number | null;
  products: string[];
  need: string | null;
  objections: { label: string; answered: boolean }[];
  /** Whether each step was explored: what was asked, or what could have been. */
  spin: Record<SpinStep, { done: boolean; text: string | null }> | null;
  feedback: { good: string | null; improve: string | null };
  /** What the supervision should ask of the seller. */
  nextStep: string | null;
  evidence: { quote: string; when: string }[];
  confidence: number | null;
}

/** Something the conversations show that the CRM does not have. */
export interface UsageGap {
  key: string;
  title: string;
  action: string;
  conversations: number;
  example: Evidence | null;
}

export interface IntelligenceReport {
  generatedAt: string;
  /** Local date, `YYYY-MM-DD`. */
  day: string;
  from: string;
  to: string;
  /** Which parts the AI did: `read` = the reader, `written` = the writer. */
  coverage: { read: boolean; written: boolean };
  analyzed: {
    conversations: number;
    readByAi: number;
    byRule: number;
    notRead: number;
    audios: number;
    transcribedAudios: number;
  };
  /** Whether what depends on a setting or on history could be measured. */
  /** `presenceSince`: the history of presence began during this day, so it covers only part. */
  measured: { sla: boolean; presence: boolean; presenceSince?: string | null };
  /** The analysis stopped before the day ended; absent in older reports. */
  inProgress?: boolean;
  customerConversations?: number;
  templatesSent?: number;
  sentOutsideCrm: number;
  botMessages: number;
  headline: string | null;
  kpis: IntelligenceKpis;
  hours: { hour: number; received: number; sent: number }[];
  responseBands: ResponseBands;
  spin: SpinCoverage | null;
  findings: InsightCard[];
  sellers: SellerDay[];
  conversations: ConversationDay[];
  gaps: UsageGap[];
}

/* ---------------------------------------------------------------- recommendations */

export const DISMISS_MODES = ['not_now', 'never'] as const;
export type DismissMode = (typeof DISMISS_MODES)[number];

export type RecommendationCategory = 'objection' | 'setting' | 'growth' | 'conversation';

/** What the AI recommends: what the button does, or a guide to do it by hand, and what was decided. */
export interface Recommendation {
  id: string;
  category: RecommendationCategory;
  title: string;
  why: string;
  /** How to overcome an objection. */
  how: string | null;
  evidence: Evidence[];
  conversations: number;
  conversationId: string | null;
  status: 'open' | 'applied' | 'dismissed';
  /** In how many analyses it showed up. */
  seenCount: number;
  action: { id: string; label: string; automatic: boolean; allowed: boolean } | null;
  guide: CrmSetup | null;
  guideId: string | null;
  dismissMode: DismissMode | null;
  dismissReason: string | null;
  appliedLink: string | null;
  appliedAuto: boolean;
  decidedAt: string | null;
  /** What did not go as planned when applying. */
  note: string | null;
}

export const RECOMMENDATION_FILTERS = ['all', 'objection', 'setting', 'growth', 'decided'] as const;
export type RecommendationFilter = (typeof RECOMMENDATION_FILTERS)[number];

export interface RecommendationsPage {
  items: Recommendation[];
  total: number;
  /** The page answered: the last one, when the one asked for is gone. */
  page: number;
  pageSize: number;
  counts: { objection: number; setting: number; growth: number; decided: number };
}

/** How the agent works here: when it runs by itself, and whether it applies what it recommends. */
export interface AgentSettings {
  /** Off by default. */
  autoApply: boolean;
  autoTasks: boolean;
  /** Null = it only runs when someone asks. */
  schedule: {
    everyDay: boolean;
    /** 0 = Monday … 6 = Sunday. */
    weekdays: number[];
    runTime: string;
    nextRunAt?: string | null;
  } | null;
}

export type ActionValues = Record<string, string | number | boolean | null>;

export interface ActionField {
  key: string;
  label: string;
  kind: 'text' | 'textarea' | 'number' | 'select' | 'switch' | 'date';
  required?: boolean;
  help?: string;
  min?: number;
  max?: number;
  options?: { value: string; label: string }[];
  hidden?: boolean;
}

/** The form of the button: what will be done, ready to change. */
export interface RecommendationForm {
  id: string;
  title: string;
  outcome: string;
  warning: string | null;
  fields: ActionField[];
  values: ActionValues;
}

/* --------------------------------------------------------------------- evolution */

export const EVOLUTION_RANGES = [7, 30] as const;
export type EvolutionRange = (typeof EVOLUTION_RANGES)[number];

/** What a day measured, of the team or of one seller. */
export interface EvolutionPoint {
  day: string;
  conversations: number;
  messagesSent: number;
  responseSeconds: number | null;
  slaCoverage: number | null;
  waiting: number;
  onlineSeconds: number | null;
  wonCents: number;
  quality: number | null;
  spin: Record<SpinStep, number> | null;
}

/** A stretch of days summed up. */
export type EvolutionPeriod = Omit<EvolutionPoint, 'day'> & { days: number };

export interface EvolutionSeries {
  points: EvolutionPoint[];
  current: EvolutionPeriod;
  previous: EvolutionPeriod;
}

export interface IntelligenceEvolution {
  days: number;
  from: string;
  to: string;
  team: EvolutionSeries;
  sellers: (EvolutionSeries & { id: string; name: string })[];
  objections: { label: string; current: number; answered: number; previous: number }[];
}

/* ---------------------------------------------------------------- agent lifecycle */

/** Where the organization stands with the agent (spec 063 § 9.1). */
export const INTELLIGENCE_STATES = [
  'disabled',
  'ready',
  'running',
  'quota_reached',
  'requested',
] as const;
export type IntelligenceState = (typeof INTELLIGENCE_STATES)[number];

export type IntelligenceRunStatus =
  | 'queued'
  | 'preparing'
  | 'reading'
  | 'aggregating'
  | 'writing'
  | 'completed'
  | 'partial'
  | 'failed'
  | 'canceled'
  | 'skipped';

export interface IntelligenceRun {
  id: string;
  /** The schedule, someone of the organization, or the platform. */
  trigger: 'scheduled' | 'manual' | 'platform';
  status: IntelligenceRunStatus;
  /** 0–100. */
  progress: number;
  windowFrom: string;
  windowTo: string;
  conversationsTotal: number;
  conversationsRead: number;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  errorCode: string | null;
}

/** A day that has a report; the newest analysis of the day is the one shown. */
export interface ReportDay {
  /** Local date, `YYYY-MM-DD`. */
  day: string;
  runId: string;
  trigger: IntelligenceRun['trigger'];
  status: IntelligenceRunStatus;
  /** False = an analysis of the day while it was going on. */
  wholeDay: boolean;
}

export type IntelligenceFrequency = 'daily' | 'weekly' | 'biweekly' | 'monthly';

export interface IntelligenceStatus {
  state: IntelligenceState;
  /** The run going on now. */
  run: IntelligenceRun | null;
  /** The newest finished run: its report (a day) is the one shown. */
  lastRun: IntelligenceRun | null;
  request: { status: 'pending' | 'in_contact'; createdAt: string } | null;
  /** When it runs by itself; null = only when someone asks for it. */
  schedule: {
    frequency: IntelligenceFrequency;
    weekday: number | null;
    /** More than one day of a weekly schedule. */
    weekdays: number[];
    dayOfMonth: number | null;
    runTime: string | null;
    nextRunAt: string | null;
  } | null;
  /** The analyses of this month: the free ones, the extra granted, the used. */
  quota: { free: number; granted: number; used: number; remaining: number };
  canRun: boolean;
  canRequest: boolean;
}

export const RequestAgentSchema = z.object({
  message: z.string().trim().max(2000),
  contactPhone: z.string().trim().max(40),
});
export type RequestAgentForm = z.infer<typeof RequestAgentSchema>;
